/**
 * Venue search and sync with Algolia.
 * All index/delete and search go through Cloud Functions (src/api/algoliaApi.js).
 * No Algolia write or search keys on the frontend.
 * See docs/VENUE_ALGOLIA_DATA_STORE.md for index schema.
 */

import { getAlgoliaAppId } from '../config/algolia';
import * as algoliaApi from '../api/algoliaApi';

/**
 * Build Algolia record from Firestore venue (no slots/bookings).
 * objectID = venue.algoliaObjectID || venue.id (so claimed venues update existing Algolia record).
 * verified = true when venue has an owner/manager (claimed).
 */
export function venueToAlgoliaRecord(venue) {
  if (!venue || !venue.id) return null;
  const courts = venue.courts || [];
  const gameIds = venue.gameIds || [];
  const courtGameIds = new Set();
  courts.forEach((c) => (c.gameIds || []).forEach((g) => courtGameIds.add(g)));
  const gamesSupported = [...new Set([...gameIds, ...courtGameIds])];
  const objectID = venue.algoliaObjectID || venue.id;
  const verified = !!(venue.createdBy || (venue.managerIds && venue.managerIds.length > 0));
  const lat = venue.latitude != null ? Number(venue.latitude) : null;
  const lng = venue.longitude != null ? Number(venue.longitude) : null;
  const record = {
    objectID,
    name: venue.name || '',
    address: venue.address || '',
    pincode: (venue.pincode || '').toString().trim(),
    city: (venue.city || '').toString().trim(),
    area: (venue.area || '').toString().trim(),
    description: venue.description || '',
    phone: venue.phone || '',
    mapUrl: venue.mapUrl || '',
    website: (venue.website || '').toString().trim() || null,
    formattedAddress: (venue.formattedAddress || venue.address || '').toString().trim() || null,
    latitude: lat,
    longitude: lng,
    placeId: (venue.placeId || '').toString().trim() || null,
    gameIds: Array.isArray(gameIds) ? gameIds : [],
    courtCount: courts.length,
    courts: courts.map((c) => ({
      id: c.id,
      name: c.name || '',
      gameIds: c.gameIds || [],
    })),
    gamesSupported,
    verified,
    createdBy: venue.createdBy || null,
    managerIds: Array.isArray(venue.managerIds) ? venue.managerIds : [],
    updatedAt: Math.floor(Date.now() / 1000),
  };
  if (lat != null && lng != null && !Number.isNaN(lat) && !Number.isNaN(lng)) {
    record._geoloc = { lat, lng };
  }
  return record;
}

/**
 * Search venues via backend. Returns { hits, queryID }. Backend normalizes hits.
 */
export async function searchVenues(query, options = {}) {
  try {
    const result = await algoliaApi.searchVenues(query || '', { gameId: options.gameId, hitsPerPage: options.hitsPerPage || 20 });
    return result || { hits: [], queryID: null };
  } catch (err) {
    console.error('Algolia venue search error:', err);
    return { hits: [], queryID: null };
  }
}

/**
 * Save (index) one venue to Algolia via backend. Call after create/update in Firestore.
 */
export async function saveVenueToAlgolia(venue) {
  const record = venueToAlgoliaRecord(venue);
  if (!record) return;
  try {
    await algoliaApi.venueIndex(record);
  } catch (err) {
    console.error('Algolia save venue error:', err);
  }
}

/**
 * Remove venue from Algolia via backend (e.g. when deleted in Firestore).
 */
export async function deleteVenueFromAlgolia(venueId) {
  if (!venueId) return;
  try {
    await algoliaApi.venueDelete(venueId);
  } catch (err) {
    console.error('Algolia delete venue error:', err);
  }
}

/** True when Algolia venue search is available (backend handles search; appId used for UI only). */
export function isAlgoliaConfigured() {
  return !!getAlgoliaAppId();
}
